<script setup lang="ts">
import KgrIcon from '@shared/components/KgrIcon.vue';
import KgrModal from '@shared/components/KgrModal.vue';
import { useAuthImage } from '@shared/composables/useAuthImage';
import { computed, ref, toRef } from 'vue';

// View-only-Portierung der styleguide-Komponente `kgr-profile-image`:
//  - src vorhanden → Bild (auth-fetch via KgrAuthImage)
//  - sonst name    → Initialen
//  - sonst         → Geschlechts-Silhouette (Angular rendert dies, wenn — wie in
//    der person-card — kein name, aber ein gender übergeben wird)
/**
 * Diese SFC hat ZWEI Wurzelknoten (die Bild-Box und das optionale Gross-Modal). Vue vererbt
 * Attribute an ein Fragment nicht automatisch und laesst uebergebene Klassen **lautlos** fallen —
 * `class="selectedMemberProfileImage kgr-postfix__icon"` kam dadurch an keinem Element an, und die
 * zugehoerige Regel (`margin-left: -10px`, ueberlappende Avatare) blieb wirkungslos. Angular setzt
 * dieselben Klassen auf den Host von `kgr-profile-image`; das stellen die beiden Zeilen hier wieder
 * her, ohne die DOM-Struktur zu aendern. Gefunden am 17.09.2026 ueber eine Interaktions-Messung —
 * weder Baseline noch Quelltext-Review konnten es zeigen.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        src?: string | null
        name?: string | null
        gender?: string | null
        size?: 'xs' | 's' | 'm' | 'l' | 'xl'
        inline?: boolean
        /** Angular `largeImageUrl$`: when set, clicking the photo opens it full size in a modal. */
        largeSrc?: string | null
        /** Angular `[segment]`: picks one of the styleguide's rotating background colors. */
        segment?: number | null
    }>(),
    { src: null, name: null, gender: null, size: 'm', inline: false, largeSrc: null, segment: null },
);

// entspricht GenderCodeToIconMap in profile-image.component.ts
const genderIconMap: Record<string, string> = {
    'gender.m': 'user-male',
    'gender.f': 'user-female',
    'gender.u': 'user',
    'gender.v': 'user',
    'child.gender.m': 'user-male',
    'child.gender.f': 'user-female',
};

const genderIcon = computed(() => (props.gender ? genderIconMap[props.gender] : null) ?? 'user');
const initials = computed(() =>
    props.name
        ? props.name
                .trim()
                .toUpperCase()
                .split(' ')
                .map((n) => n[0])
                .join('')
        : '',
);
const sizeClass = computed(() => `kgr-profileImage--size${props.size.toUpperCase()}`);

// Gate the photo on the actually loaded blob, not on the presence of `src` — mirrors Angular's
// kgr-profile-image (`@if (imageUrl)`). While loading, or if the auth fetch fails (e.g. a 403
// for a child whose avatar the current user may not read), we fall back to initials / the
// gender silhouette instead of leaving an empty box.
const objectUrl = useAuthImage(toRef(props, 'src'));

// Angular `showLargeIfAvailable()`: only clickable when a large variant was supplied. The XL image
// is fetched lazily — the modal shows the loading container until the blob is there.
const showLarge = ref(false);
const largeRequestUrl = computed(() => (showLarge.value ? props.largeSrc : null));
const largeObjectUrl = useAuthImage(largeRequestUrl);

function showLargeIfAvailable() {
    if (props.largeSrc) {
        showLarge.value = true;
    }
}
</script>

<template>
    <div
        v-bind="$attrs"
        class="kgr-profileImage"
        :class="[sizeClass, segment ? `is-segment-${segment}` : null, { 'kgr-profileImage--inline': inline }]"
    >
        <img
            v-if="objectUrl"
            :src="objectUrl"
            :alt="name ?? ''"
            class="kgr-profileImage__image"
            :class="{ 'kgr-clickable': !!largeSrc }"
            @click="showLargeIfAvailable"
        />
        <div
            v-else-if="name"
            class="kgr-profileImage__image"
            :title="name"
        >
            {{ initials }}
        </div>
        <KgrIcon
            v-else
            :name="genderIcon"
            class="kgr-profileImage__image"
        />
    </div>

    <KgrModal
        v-if="showLarge"
        :open="showLarge"
        :close-on-backdrop-click="true"
        @close="showLarge = false"
    >
        <div class="kgr-modal__content kgr-region">
            <div
                class="kgr-profileImage kgr-profileImage--sizeXL kgr-loadingContainer"
                :class="{ 'is-loading': !largeObjectUrl }"
            >
                <img
                    v-if="largeObjectUrl"
                    :src="largeObjectUrl"
                    :alt="name ?? ''"
                    class="kgr-profileImage__image"
                />
            </div>
        </div>
    </KgrModal>
</template>
